// Who is on a project RIGHT NOW, as chips beside the stage badge.
//
// `project-stage.ts` answers "how far did it get" and is CUMULATIVE: rungs are
// artifacts, and artifacts do not un-happen. That is the right shape for a
// funnel and the wrong shape for triage, because a project whose team took the
// conversation over produces no artifact and so reads "Brief" forever, exactly
// as if nobody had touched it (Monday 3143611422).
//
// So these live BESIDE the badge rather than on the ladder. Each one is current
// and reversible; none is a step towards anything; and a row can carry any
// combination of them, including none, which is the common case.
//
// Backend twin: `recruiter/repo/project_handling.py`.
import type { ExplorerProjectRow } from "./api";

/** Every handling state, in the order the chips render. Ordered by how loudly
 *  it should interrupt someone scanning the column: a human on the client
 *  outranks a parked agent, which outranks work merely in flight. */
export const HANDLING_KEYS = ["human_takeover", "agent_paused", "search_running"] as const;

export type HandlingKey = (typeof HANDLING_KEYS)[number];

/** The Badge axes these chips use. Declared locally, same as `project-stage.ts`
 *  and `alert-cell.tsx` do for theirs. */
type HandlingTone = "neutral" | "info" | "warning";
type HandlingVariant = "outline" | "soft" | "solid";

export interface HandlingChip {
  key: HandlingKey;
  label: string;
  tone: HandlingTone;
  variant: HandlingVariant;
  /** The badge's tooltip. These states are invisible everywhere else on the
   *  row, so an operator who wonders what "Agent paused" costs the client gets
   *  the answer on hover rather than from the code. */
  hint: string;
}

/** The subset of a project row these chips read. Taken as a structural type so
 *  the Explorer's own `ProjectRow` and the wire `ExplorerProjectRow` both
 *  satisfy it without either importing the other's shape. */
export type HandlingSource = Pick<
  ExplorerProjectRow,
  "humanTakeover" | "humanTakeoverBy" | "agentPaused" | "searchRunning"
>;

/** The sentinel `web/takeover.py` writes when the automatic big-budget engage
 *  fired and no human pressed anything. Kept verbatim rather than prettified
 *  into a name: an audit line that relabels an automatic action as a person's
 *  is worse than one that looks slightly raw. */
export const AUTO_TAKEOVER_ACTOR = "mira-auto-stop";

/** Who took a project over, phrased for a tooltip. */
function takeoverHint(by: string | null | undefined): string {
  if (!by) return "Someone told this client a person would reach out. Mira keeps running.";
  if (by === AUTO_TAKEOVER_ACTOR) {
    return "Handed over automatically on a big budget, not by a person. Mira keeps running.";
  }
  return `${by} told this client a person would reach out. Mira keeps running.`;
}

/**
 * The chips for one row, loudest first. Empty when nothing is happening, which
 * is most rows: the Stage cell then renders the badge alone, exactly as before.
 *
 * Every field is OPTIONAL on the wire (an older backend, and the support-mode
 * payload, carry none of them), and an absent field reads as false rather than
 * as an unknown. That is deliberate: the alternative is a "handling unknown"
 * chip on every row of a console talking to a backend that predates the field,
 * which is noise about us rather than information about the client.
 */
export function handlingChips(row: HandlingSource): HandlingChip[] {
  const chips: HandlingChip[] = [];
  if (row.humanTakeover) {
    chips.push({
      key: "human_takeover",
      // The same words the Big clients desk badges this state with, so one
      // project cannot read "Human-handled" on one page and something else on
      // another.
      label: "Human-handled",
      tone: "warning",
      variant: "soft",
      hint: takeoverHint(row.humanTakeoverBy),
    });
  }
  if (row.agentPaused) {
    chips.push({
      key: "agent_paused",
      label: "Agent paused",
      tone: "neutral",
      variant: "solid",
      // Worth spelling out: a takeover does NOT pause anything (blocking was
      // removed 2026-08-10), so this chip is the only one that means the
      // client is talking to a stopped agent.
      hint: "Mira is parked on this project. The client gets no reply until someone resumes her.",
    });
  }
  if (row.searchRunning) {
    chips.push({
      key: "search_running",
      label: "Searching",
      tone: "info",
      variant: "outline",
      hint: "A talent search is running right now. It is finished once this clears.",
    });
  }
  return chips;
}

/**
 * The value extractor for the Handling filter column.
 *
 * Returns the keys a row carries (a row can be several at once, so ticking any
 * one of them keeps it), or null for "nothing is happening" — which the filter
 * offers as its own bucket. Null rather than an empty array on purpose: the
 * shared filter treats null as the no-value case and labels it, and "nobody is
 * on this one" is an answer an operator actively looks for.
 */
export function handlingFilterValue(row: HandlingSource): HandlingKey[] | null {
  const keys = handlingChips(row).map((chip) => chip.key);
  return keys.length > 0 ? keys : null;
}

/** Filter-option labels. Reads the chip vocabulary rather than a second copy of
 *  it, so a renamed chip renames its filter option too. */
export function handlingLabel(key: string): string {
  const chip = handlingChips({
    humanTakeover: key === "human_takeover",
    agentPaused: key === "agent_paused",
    searchRunning: key === "search_running",
  })[0];
  return chip?.label ?? key.replaceAll("_", " ");
}
